import {
  useSceneEvents,
  type SurfaceWandAddState,
} from "@/components/common/scene-events-context";
import {
  FaroTooltip,
  ToolButton,
  ToolGroup,
  Toolbar,
} from "@faro-lotv/flat-ui";
import AddIcon from "@mui/icons-material/Add";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import StorageIcon from "@mui/icons-material/Storage";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import StopIcon from "@mui/icons-material/Stop";
import { useEffect, useState, type JSX } from "react";
import { Box, Slider, Stack, Typography } from "@mui/material";
import { grey } from "@faro-lotv/flat-ui";
import { NoTranslate } from "@faro-lotv/app-component-toolbox";
import { formatArea } from "@/components/common/settings-context/distance-unit-utils";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import type { SurfaceReportStatus } from "./surface-report";
import { SurfaceReportDialog } from "./surface-report-dialog";

/** @returns Wand actions using the same submenu toolbar as the clipping box. */
export function SurfaceWandMenu(): JSX.Element {
  const events = useSceneEvents();
  const unit = useDistanceUnit();
  const [report, setReport] = useState<SurfaceReportStatus>({ phase: "idle" });
  useEffect(() => {
    const listener = events.surfaceWandReportState.on(setReport);
    return () => {
      listener.dispose();
      events.surfaceWandReport.emit("cancel");
    };
  }, [events]);
  const [state, setState] = useState<SurfaceWandAddState>({
    enabled: false,
    armed: false,
    canClear: false,
    count: 0,
    totalArea: 0,
    otherPointsOpacity: 1,
  });
  useEffect(() => {
    const subscription = events.surfaceWandAddSeedState.on(setState);
    events.surfaceWandAddSeed.emit("status");
    return () => subscription.dispose();
  }, [events]);
  const addLabel = {
    plane: "Add seed to selected plane",
    cylinder: "Add seed to selected cylinder",
    "pipe-run": "Add seed to selected pipe run",
  }[state.shape ?? "plane"];
  const label = state.armed ? "Cancel adding seed" : addLabel;
  return (
    <>
      <Toolbar sx={{ width: "max-content" }}>
        <Stack sx={{ width: 208 }}>
          <ToolGroup
            orientation="horizontal"
            role="group"
            aria-label="Surface shape"
          >
            <FaroTooltip title="Plane selection">
              <ToolButton
                aria-label="Plane selection"
                aria-pressed={!state.shape || state.shape === "plane"}
                selected={!state.shape || state.shape === "plane"}
                onClick={() =>
                  events.surfaceWandAddSeed.emit({ shape: "plane" })
                }
              >
                <CropSquareIcon />
              </ToolButton>
            </FaroTooltip>
            <FaroTooltip title="Cylinder selection">
              <ToolButton
                aria-label="Cylinder selection"
                aria-pressed={state.shape === "cylinder"}
                selected={state.shape === "cylinder"}
                onClick={() =>
                  events.surfaceWandAddSeed.emit({ shape: "cylinder" })
                }
              >
                <StorageIcon />
              </ToolButton>
            </FaroTooltip>
            <FaroTooltip title="Pipe run selection">
              <ToolButton
                aria-label="Pipe run selection"
                aria-pressed={state.shape === "pipe-run"}
                selected={state.shape === "pipe-run"}
                onClick={() =>
                  events.surfaceWandAddSeed.emit({ shape: "pipe-run" })
                }
              >
                <AltRouteIcon />
              </ToolButton>
            </FaroTooltip>
          </ToolGroup>
          <ToolGroup
            orientation="horizontal"
            role="toolbar"
            aria-label="Magic wand"
          >
            <FaroTooltip
              title={
                state.shape === "pipe-run"
                  ? "Additional pipe seeds are not yet available"
                  : label
              }
            >
              <ToolButton
                aria-label={label}
                aria-pressed={state.armed}
                selected={state.armed}
                disabled={!state.enabled}
                onClick={() => events.surfaceWandAddSeed.emit("toggle")}
              >
                <AddIcon />
              </ToolButton>
            </FaroTooltip>
            <FaroTooltip title="New selection">
              <ToolButton
                aria-label="New selection"
                disabled={state.count === 0}
                onClick={() => events.surfaceWandAddSeed.emit("new")}
              >
                <NoteAddIcon />
              </ToolButton>
            </FaroTooltip>
            <FaroTooltip title="Clear all selections">
              <ToolButton
                aria-label="Clear all selections"
                disabled={!state.canClear}
                onClick={() => events.surfaceWandAddSeed.emit("clear")}
              >
                <DeleteSweepIcon />
              </ToolButton>
            </FaroTooltip>
            <FaroTooltip
              title={
                state.hasPipeRuns
                  ? "Pipe run reports are not yet available"
                  : "Create report"
              }
            >
              <ToolButton
                aria-label="Create report"
                disabled={!state.canReport || report.phase === "loading"}
                onClick={() => events.surfaceWandReport.emit("create")}
              >
                <PictureAsPdfIcon />
              </ToolButton>
            </FaroTooltip>
          </ToolGroup>
          {state.canStop && (
            <ToolGroup orientation="horizontal">
              <FaroTooltip title="Stop tracing">
                <ToolButton
                  aria-label="Stop tracing"
                  onClick={() => events.surfaceWandAddSeed.emit("stop")}
                >
                  <StopIcon />
                </ToolButton>
              </FaroTooltip>
            </ToolGroup>
          )}
          <Box sx={{ px: 1.5, pb: 1, color: grey[0] }}>
            <Typography
              component="label"
              htmlFor="wand-background-opacity"
              sx={{ fontSize: 12 }}
            >
              Other points
            </Typography>
            <Slider
              aria-label="Other points opacity"
              slotProps={{ input: { id: "wand-background-opacity" } }}
              size="small"
              disabled={state.count === 0}
              min={0}
              max={1}
              step={0.01}
              value={state.otherPointsOpacity}
              onChange={(_, value) => {
                if (typeof value === "number") {
                  events.surfaceWandAddSeed.emit({ otherPointsOpacity: value });
                }
              }}
            />
            <Stack
              direction="row"
              sx={{ justifyContent: "space-between", gap: 1 }}
            >
              <Typography sx={{ fontSize: 12 }}>
                {state.hasPipeRuns
                  ? "Plane/cylinder area"
                  : "Total surface area"}
              </Typography>
              <Typography
                component="output"
                aria-label={
                  state.hasPipeRuns
                    ? "Plane/cylinder area"
                    : "Total surface area"
                }
                sx={{
                  fontSize: 12,
                  fontVariantNumeric: "tabular-nums",
                  overflowWrap: "anywhere",
                  textAlign: "right",
                }}
              >
                <NoTranslate component="span">{`~${formatArea(state.totalArea, unit)}`}</NoTranslate>
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Toolbar>
      <SurfaceReportDialog
        status={report}
        onClose={() => {
          events.surfaceWandReport.emit("cancel");
          setReport({ phase: "idle" });
        }}
        onRetry={() => events.surfaceWandReport.emit("create")}
      />
    </>
  );
}
